import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  TranscriptionJob,
  QueueStatistics,
  JobFilter,
  JobSort,
  PaginationParams,
  JobListResponse,
  BulkActionRequest,
  BulkActionResponse,
  WorkerStatus,
} from "../types";
import { transcriptionQueueApi } from "../api/transcriptionQueueApi";

/**
 * Smart merge that preserves existing values when new values are empty/null/undefined
 */
function mergeJobData(
  existing: TranscriptionJob,
  updated: TranscriptionJob,
): TranscriptionJob {
  const merged = { ...existing } as Record<string, unknown>;

  // Only update fields that have meaningful values
  Object.keys(updated).forEach((key) => {
    const typedKey = key as keyof TranscriptionJob;
    const newValue = updated[typedKey];
    const existingValue = existing[typedKey];

    // Check if new value is empty (null, undefined, empty string, or empty array)
    const isNewValueEmpty =
      newValue === null ||
      newValue === undefined ||
      newValue === "" ||
      (Array.isArray(newValue) && newValue.length === 0);

    const isExistingValueEmpty =
      existingValue === null ||
      existingValue === undefined ||
      existingValue === "" ||
      (Array.isArray(existingValue) && existingValue.length === 0);

    // Update if new value is not empty
    if (!isNewValueEmpty) {
      merged[key] = newValue;
    } else if (isExistingValueEmpty) {
      // If both are empty, use the new value (even if empty)
      merged[key] = newValue;
    }
    // Otherwise, preserve the existing value
  });

  return merged as unknown as TranscriptionJob;
}

export interface QueueState {
  jobs: TranscriptionJob[];
  selectedJob: TranscriptionJob | null;
  statistics: QueueStatistics | null;
  workerStatus: WorkerStatus | null;
  filter: JobFilter;
  sort: JobSort;
  pagination: PaginationParams;
  loading: boolean;
  showLoadingIndicator: boolean;
  error: string | null;
  selectedJobIds: string[];
  lastUpdate: string | null;
}

const initialState: QueueState = {
  jobs: [],
  selectedJob: null,
  statistics: null,
  workerStatus: null,
  filter: {},
  sort: {
    field: "created",
    direction: "desc",
  },
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  showLoadingIndicator: false,
  error: null,
  selectedJobIds: [],
  lastUpdate: null,
};

export const fetchJobs = createAsyncThunk<
  JobListResponse,
  void,
  { state: { transcriptionQueue: QueueState } }
>("transcriptionQueue/fetchJobs", async (_, { getState }) => {
  const { filter, sort, pagination } = getState().transcriptionQueue;
  const response = await transcriptionQueueApi.getJobs({
    filter,
    sort,
    pagination,
  });
  return response;
});

export const fetchJobDetails = createAsyncThunk<TranscriptionJob, string>(
  "transcriptionQueue/fetchJobDetails",
  async (jobId) => {
    const response = await transcriptionQueueApi.getJobDetails(jobId);
    return response;
  },
);

export const retryJob = createAsyncThunk<TranscriptionJob, string>(
  "transcriptionQueue/retryJob",
  async (jobId) => {
    const response = await transcriptionQueueApi.retryJob(jobId);
    return response;
  },
);

export const cancelJob = createAsyncThunk<void, string>(
  "transcriptionQueue/cancelJob",
  async (jobId) => {
    await transcriptionQueueApi.cancelJob(jobId);
  },
);

export const performBulkAction = createAsyncThunk<
  BulkActionResponse,
  BulkActionRequest
>("transcriptionQueue/performBulkAction", async (request) => {
  const response = await transcriptionQueueApi.performBulkAction(request);
  return response;
});

export const fetchWorkerStatus = createAsyncThunk<WorkerStatus>(
  "transcriptionQueue/fetchWorkerStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await transcriptionQueueApi.getWorkerStatus();
      console.log("Worker status fetched:", response);
      return response;
    } catch (error) {
      console.error("Failed to fetch worker status:", error);
      return rejectWithValue("Failed to fetch worker status");
    }
  },
);

export const startWorker = createAsyncThunk<
  { success: boolean; message: string; pending_count: number },
  number | undefined
>("transcriptionQueue/startWorker", async (limit = 10, { rejectWithValue }) => {
  try {
    const response = await transcriptionQueueApi.startWorker(limit);
    return response;
  } catch (error) {
    console.error("Failed to start worker:", error);
    return rejectWithValue("Failed to start worker");
  }
});

const queueSlice = createSlice({
  name: "transcriptionQueue",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<JobFilter>) => {
      state.filter = action.payload;
      state.pagination.page = 1;
    },
    setSort: (state, action: PayloadAction<JobSort>) => {
      state.sort = action.payload;
      state.pagination.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
    selectJob: (state, action: PayloadAction<string>) => {
      const job = state.jobs.find((j) => j.id === action.payload);
      if (job) {
        state.selectedJob = job;
      }
    },
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },
    toggleJobSelection: (state, action: PayloadAction<string>) => {
      const index = state.selectedJobIds.indexOf(action.payload);
      if (index > -1) {
        state.selectedJobIds.splice(index, 1);
      } else {
        state.selectedJobIds.push(action.payload);
      }
    },
    selectAllJobs: (state) => {
      state.selectedJobIds = state.jobs.map((job) => String(job.id));
    },
    clearJobSelection: (state) => {
      state.selectedJobIds = [];
    },
    updateJobFromWebSocket: (
      state,
      action: PayloadAction<TranscriptionJob>,
    ) => {
      const index = state.jobs.findIndex((j) => j.id === action.payload.id);
      if (index > -1) {
        // Merge new data with existing to preserve all fields from list view
        state.jobs[index] = mergeJobData(state.jobs[index], action.payload);
      } else if (
        state.filter.status === undefined ||
        state.filter.status.includes(action.payload.status)
      ) {
        state.jobs.unshift(action.payload);
      }

      if (state.selectedJob?.id === action.payload.id) {
        state.selectedJob = mergeJobData(state.selectedJob, action.payload);
      }

      state.lastUpdate = new Date().toISOString();
    },
    updateStatisticsFromWebSocket: (
      state,
      action: PayloadAction<QueueStatistics>,
    ) => {
      state.statistics = action.payload;
      state.lastUpdate = new Date().toISOString();
    },
    setShowLoadingIndicator: (state, action: PayloadAction<boolean>) => {
      state.showLoadingIndicator = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.showLoadingIndicator = false;
        state.jobs = action.payload.jobs;
        state.pagination = action.payload.pagination;
        state.statistics = action.payload.statistics;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.showLoadingIndicator = false;
        state.error = action.error.message || "Failed to fetch jobs";
      })
      .addCase(fetchJobDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Don't clear selectedJob to avoid flashing empty state
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.loading = false;

        console.log("fetchJobDetails.fulfilled - payload:", action.payload);
        console.log(
          "fetchJobDetails.fulfilled - jobs in state:",
          state.jobs.length,
        );

        // Find the existing job in the list to preserve all fields
        // Use loose equality to handle string/number ID mismatch
        const existingJobInList = state.jobs.find(
          (j) => String(j.id) === String(action.payload.id),
        );

        console.log(
          "fetchJobDetails.fulfilled - existingJobInList:",
          existingJobInList ? "FOUND" : "NOT FOUND",
        );

        // Merge with existing data to preserve all fields from list view
        if (existingJobInList) {
          state.selectedJob = mergeJobData(existingJobInList, action.payload);
        } else {
          // If not found in list, use the payload directly
          state.selectedJob = action.payload;
        }

        console.log(
          "fetchJobDetails.fulfilled - selectedJob set to:",
          state.selectedJob,
        );

        // Also update the job in the list
        const index = state.jobs.findIndex(
          (j) => String(j.id) === String(action.payload.id),
        );
        if (index > -1) {
          // Merge new data with existing to preserve all fields from list view
          state.jobs[index] = mergeJobData(state.jobs[index], action.payload);
        }
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job details";
        console.error("fetchJobDetails.rejected - error:", action.error);
        console.error("fetchJobDetails.rejected - meta:", action.meta);
      })
      .addCase(retryJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(retryJob.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.jobs.findIndex((j) => j.id === action.payload.id);
        if (index > -1) {
          // Merge new data with existing to preserve all fields from list view
          state.jobs[index] = mergeJobData(state.jobs[index], action.payload);
        }
        if (state.selectedJob?.id === action.payload.id) {
          state.selectedJob = mergeJobData(state.selectedJob, action.payload);
        }
      })
      .addCase(retryJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to retry job";
      })
      .addCase(cancelJob.fulfilled, (state, action) => {
        const jobId = action.meta.arg;
        const index = state.jobs.findIndex((j) => j.id === jobId);
        if (index > -1) {
          state.jobs[index].status = "failed";
        }
      })
      .addCase(performBulkAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performBulkAction.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJobIds = [];
      })
      .addCase(performBulkAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to perform bulk action";
      })
      .addCase(fetchWorkerStatus.fulfilled, (state, action) => {
        state.workerStatus = action.payload;
      });
  },
});

export const {
  setFilter,
  setSort,
  setPage,
  setPageSize,
  selectJob,
  clearSelectedJob,
  toggleJobSelection,
  selectAllJobs,
  clearJobSelection,
  updateJobFromWebSocket,
  updateStatisticsFromWebSocket,
  setShowLoadingIndicator,
  clearError,
} = queueSlice.actions;

export default queueSlice.reducer;
